import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import AccessWarningModal from '../common/AccessWarningModal';

const ACCESS_FILTERS = ['all', 'approved', 'pending', 'rejected', 'revoked_by_user', 'closed_by_psychologist', 'no_request'];

function formatClosedBy(role) {
  if (role === 'user') return 'пользователем';
  if (role === 'psychologist') return 'вами (психологом)';
  return role || '—';
}

function PsychologistUsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPsychologistUsers({
        search: search.trim(),
        assignment: assignmentFilter,
        access: accessFilter,
      });
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const canSearch = useMemo(() => !loading, [loading]);

  const handleAssign = async (userId) => {
    await api.assignPsychologistUser(userId);
    await loadUsers();
  };

  const openRequestAccess = (user) => {
    setDialog({
      type: 'request',
      user,
      title: 'Запросить доступ к материалам',
      warning:
        'Пользователь получит уведомление в виде запроса в личном кабинете. Доступ к результатам тестов возможен только после явного согласия пользователя. Не злоупотребляйте запросами.',
      confirmLabel: 'Отправить запрос',
      confirmButtonClass: 'btn-success',
      showOptionalMessage: true,
      optionalMessageLabel: 'Сообщение пользователю (необязательно)',
    });
  };

  const openCloseAccess = (user) => {
    setDialog({
      type: 'close',
      user,
      title: 'Закрыть доступ',
      warning:
        'Вы добровольно прекращаете доступ к материалам этого пользователя. Пользователь увидит, что доступ закрыт психологом, и причину (если укажете). Повторный просмотр материалов потребует нового одобрения.',
      confirmLabel: 'Закрыть доступ',
      confirmButtonClass: 'btn-danger',
      reasonRequired: true,
      reasonLabel: 'Причина закрытия',
      reasonPlaceholder: 'Эта причина будет доступна пользователю.',
    });
  };

  const openReleaseClient = (user) => {
    setDialog({
      type: 'release',
      user,
      title: 'Освободить клиента',
      warning:
        'Вы завершаете работу с этим пользователем. Назначение будет снято, но доступ к материалам (если был одобрен) сохранится до его закрытия. Укажите причину завершения работы.',
      confirmLabel: 'Освободить',
      confirmButtonClass: 'btn-warning',
      reasonRequired: true,
      reasonLabel: 'Причина завершения работы',
      reasonPlaceholder: 'Например: завершение терапии, передача другому специалисту.',
    });
  };

  const handleModalConfirm = async ({ reason, optionalMessage }) => {
    if (dialog.type === 'request') {
      await api.requestMaterialsAccess(dialog.user._id, {
        scope: ['tests'],
        messageFromPsychologist: optionalMessage,
      });
    } else if (dialog.type === 'close') {
      await api.closeMaterialsAccess(dialog.user.accessRequestId, reason);
    } else if (dialog.type === 'release') {
      await api.releasePsychologistUser(dialog.user._id, reason);
    }
    await loadUsers();
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3">Пользователи системы</h2>
      <p className="text-muted">Навигация психолога по пользователям и доступам к материалам.</p>

      <div className="ui-card p-3 mb-3 d-flex flex-wrap gap-2">
        <input
          type="text"
          className="form-control max-w-320"
          placeholder="Поиск: username / email / имя"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          disabled={!canSearch}
        />
        <select className="form-select max-w-220" value={assignmentFilter} onChange={(event) => setAssignmentFilter(event.target.value)}>
          <option value="all">Назначения: все</option>
          <option value="mine">В работе у меня</option>
          <option value="in_progress">В работе</option>
          <option value="unassigned">Не взяты в работу</option>
        </select>
        <select className="form-select max-w-220" value={accessFilter} onChange={(event) => setAccessFilter(event.target.value)}>
          {ACCESS_FILTERS.map((item) => (
            <option key={item} value={item}>
              Доступ: {item}
            </option>
          ))}
        </select>
        <button type="button" className="ui-btn ui-btn-primary" onClick={loadUsers}>
          Применить
        </button>
      </div>

      {loading && <div className="alert alert-info">Загрузка пользователей...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <div className="ui-card p-3">
          {users.length === 0 ? (
            <div className="alert alert-secondary mb-0">Пользователи не найдены.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 psychologist-users-table">
                <colgroup>
                  <col className="col-min-12" />
                  <col className="col-min-10" />
                  <col className="col-min-8" />
                  <col className="col-min-14" />
                  <col className="col-min-11" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Пользователь</th>
                    <th>Статус в работе</th>
                    <th>Статус доступа</th>
                    <th>Закрытие / причина</th>
                    <th className="text-md-end">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td className="align-middle">
                        <div className="text-break">
                          <strong>{user.username}</strong>
                        </div>
                        <div className="text-muted small">{user.email || 'email не указан'}</div>
                      </td>
                      <td className="align-middle">
                        <span className="badge bg-secondary text-wrap">{user.assignmentStatus}</span>
                        {user.assignedPsychologist && (
                          <div className="text-muted small mt-1 text-break">{user.assignedPsychologist.username}</div>
                        )}
                      </td>
                      <td className="align-middle">
                        <span className="badge bg-info text-wrap">{user.accessStatus}</span>
                      </td>
                      <td className="align-middle small">
                        {user.accessClosedAt ? (
                          <>
                            <div>{new Date(user.accessClosedAt).toLocaleString('ru-RU')}</div>
                            <div className="text-muted">Кто закрыл: {formatClosedBy(user.accessClosedByRole)}</div>
                            {user.accessCloseReason ? <div className="mt-1 text-break">{user.accessCloseReason}</div> : <div className="text-muted">—</div>}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle text-md-end">
                        <div className="d-flex flex-column gap-2 align-items-stretch align-items-md-end">
                          <Link
                            to={`/psychologist/users/${user._id}`}
                            className="btn btn-sm btn-outline-primary text-decoration-none text-nowrap"
                          >
                            Профиль
                          </Link>
                          {!user.isAssignedToMe && (
                            <button type="button" className="btn btn-sm btn-outline-primary text-nowrap" onClick={() => handleAssign(user._id)}>
                              Взять в работу
                            </button>
                          )}
                          {user.isAssignedToMe && user.assignmentStatus === 'in_progress' && (
                            <button type="button" className="btn btn-sm btn-outline-warning text-nowrap" onClick={() => openReleaseClient(user)}>
                              Освободить клиента
                            </button>
                          )}
                          {user.accessStatus !== 'approved' && (
                            <button type="button" className="btn btn-sm btn-outline-success text-nowrap" onClick={() => openRequestAccess(user)}>
                              Запросить доступ
                            </button>
                          )}
                          {user.accessStatus === 'approved' && user.accessRequestId && (
                            <button type="button" className="btn btn-sm btn-outline-danger text-nowrap" onClick={() => openCloseAccess(user)}>
                              Закрыть доступ
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <AccessWarningModal
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        title={dialog?.title || ''}
        warning={dialog?.warning || ''}
        reasonRequired={dialog?.reasonRequired || false}
        reasonLabel={dialog?.reasonLabel}
        reasonPlaceholder={dialog?.reasonPlaceholder}
        showOptionalMessage={dialog?.showOptionalMessage || false}
        optionalMessageLabel={dialog?.optionalMessageLabel}
        confirmLabel={dialog?.confirmLabel || 'Подтвердить'}
        confirmButtonClass={dialog?.confirmButtonClass || 'btn-primary'}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
}

export default PsychologistUsersPage;
